import { Reducer } from 'redux';
import { intersection, isEqual } from 'lodash';

import { ThunkFailAction, ThunkSuccessAction } from '~src/services/apiMiddleware';
import {
  SessionUserI,
  UserPermissions,
  TimezonesI,
  ContentLocalesI,
  SystemLocalesI,
  SystemLocales,
} from '~src/interfaces/session.types';
import { CompanyRespWithSettingAttributes, CompanyResp, CompanyI } from '~src/interfaces/company.interface';
import { StoreI } from '~src/rootReducer';
import { getAttributeBySlug } from '~src/modules/attributes/reducers/attributes.reducer';
import { AttrSlugType } from '~src/interfaces/attributes.types';

import * as types from './actionTypes';

export interface SessionState {
  authStatus: boolean;
  loginLoading: boolean;
  multiLoginLoading: boolean;
  signUpLoading: boolean;
  user: SessionUserI | null;
  permissions: UserPermissions[];
  company: CompanyI | null;
  accounts: {
    hasMultipleAccounts: boolean;
    tokens: string[];
  };
  redirect: string | null;
  timezones: TimezonesI;
  defaultContentLocale: string;
  contentLocales: ContentLocalesI;
  defaultSystemLocale: SystemLocales;
  systemLocales: SystemLocalesI;
  loadingRedeemToken: boolean;
  accountSuspended: boolean;
  loadingTimezones: boolean;
  loadingContentLocales: boolean;
  loadedLoacalesAndTimezones: boolean;
}

export function getInitialSessionState(): SessionState {
  return {
    authStatus: false,
    loginLoading: false,
    multiLoginLoading: false,
    signUpLoading: false,
    user: null,
    permissions: [],
    company: null,
    accounts: {
      hasMultipleAccounts: false,
      tokens: [],
    },
    redirect: null,
    timezones: {},
    defaultContentLocale: 'en_US',
    contentLocales: {},
    defaultSystemLocale: SystemLocales.EnUs,
    systemLocales: {
      de_DE: 'Deutsch',
      en_US: 'English',
    },
    loadingRedeemToken: false,
    accountSuspended: false,
    loadingTimezones: false,
    loadingContentLocales: false,
    loadedLoacalesAndTimezones: false,
  };
}

const sessionReducer: Reducer<SessionState, ThunkSuccessAction | ThunkFailAction> = (
  state = getInitialSessionState(),
  action,
) => {
  switch (action.type) {
    case types.LOGIN_SUCCESS:
      return { ...state, authStatus: true, accountSuspended: false };

    case types.LOGIN_LOADING:
      return { ...state, loginLoading: true };

    case types.LOGIN_NOT_LOADING:
      return { ...state, loginLoading: false };

    case types.SignupCompany.Request:
      return { ...state, signUpLoading: true };

    case types.SignupCompany.Success:
      return { ...state, signUpLoading: false };

    case types.SignupCompany.Failed:
      return { ...state, signUpLoading: false };

    case types.MULTI_LOGIN_LOADING:
      return { ...state, multiLoginLoading: true };

    case types.MULTI_LOGIN_NOT_LOADING:
      return { ...state, multiLoginLoading: false };

    case types.LOGIN_FAILED:
    case types.LOGGED_OUT:
      return { ...state, authStatus: false };

    case types.ACCOUNT_SUSPENDED:
      return { ...state, accountSuspended: true };

    case types.SUSPENDED_MESSAGE_DISPLAYED:
      return { ...state, accountSuspended: false };

    case types.SET_SESSION_USER:
      return { ...state, user: action.user };

    case types.PatchSessionUser.Request:
      return { ...state, loading: true };

    case types.PatchSessionUser.Success:
      return {
        ...state,
        loading: false,
        user: {
          ...(state.user as SessionUserI),
          firstname: action.response.data.firstname,
          lastname: action.response.data.lastname,
          email: action.response.data.email,
        },
      };

    case types.PatchSessionUser.Failed:
      return { ...state, loading: false };

    case types.UpdateSessionUserAttrs.Request:
      return { ...state, loading: true };

    case types.UpdateSessionUserAttrs.Success:
      return {
        ...state,
        loading: false,
        user: {
          ...(state.user as SessionUserI),
          setting_attributes: action.response.data.setting_attributes,
        },
      };
    case types.UpdateSessionUserAttrs.Failed:
      return { ...state, loading: false };

    case types.SET_USER_PERMISSIONS:
      return { ...state, permissions: action.permissions };

    case types.UPDATE_SESSION_USER_IMAGE:
      return { ...state, user: { ...state.user, imageUrl: action.imageUrl } };

    case types.UPDATE_COMPANY_IMAGE:
      return { ...state, company: { ...state.company, imageUrl: action.imageUrl } };

    case types.SET_CURRENT_COMPANY:
      return { ...state, company: action.company };

    case types.UpdateCompany.Request:
      return { ...state, loading: true };

    case types.UpdateCompany.Success:
      return {
        ...state,
        loading: false,
        company: { ...(state.company as CompanyI), name: (action.response.data as CompanyResp).name },
      };

    case types.UpdateCompany.Failed:
      return { ...state, loading: false, error: action.payload };

    case types.UpdateCompanyAttrs.Request:
      return { ...state, loading: true };

    case types.UpdateCompanyAttrs.Success:
      return {
        ...state,
        company: {
          ...state.company,
          setting_attributes: action.response.data.setting_attributes,
        },
      };

    case types.UpdateCompanyAttrs.Failed:
      return { ...state, loading: false, error: action.payload };

    case types.MULTIPLE_ACCOUNTS:
      return { ...state, accounts: { hasMultipleAccounts: true, tokens: action.tokens } };

    case types.CLEAR_MULTIPLE_ACCOUNTS:
      return { ...state, accounts: { hasMultipleAccounts: false, tokens: [] } };

    case types.GetTimeZones.Request:
      return { ...state, loadingTimezones: true };

    case types.GetTimeZones.Success:
      return {
        ...state,
        loadingTimezones: false,
        timezones: action.response.data,
        loadedLoacalesAndTimezones: isLoadedLoacalesAndTimezones(state, 'timezones'),
      };

    case types.GetTimeZones.Failed:
      return { ...state, loadingTimezones: false, error: action.payload };

    case types.UPDATE_SESSION_USER_ATTR: {
      return {
        ...state,
        user: {
          ...state.user,
          setting_attributes: {
            ...(state.user as SessionUserI).setting_attributes,
            [action.payload.key]: action.payload.value,
          },
        },
      };
    }

    case types.GetLocales.Request:
      return { ...state, loadingContentLocales: true };

    case types.GetLocales.Success:
      return {
        ...state,
        loadingContentLocales: false,
        contentLocales: action.response.data,
        loadedLoacalesAndTimezones: isLoadedLoacalesAndTimezones(state, 'locales'),
      };

    case types.GetLocales.Failed:
      return { ...state, loadingContentLocales: false, error: action.payload };

    case types.REDIRECT_SET:
      return { ...state, redirect: action.redirect };

    default:
      return state;
  }
};

export const hasPermission = (
  { user: { session } }: StoreI,
  requiredPermissions: UserPermissions[],
): boolean => {
  if (requiredPermissions === undefined || requiredPermissions === null) {
    return true;
  }
  if (session.permissions.length <= 0) {
    return false;
  }

  const hasPermissions = intersection(session.permissions, requiredPermissions);

  return isEqual(hasPermissions, requiredPermissions);
};

export const getCompanyAttrValueBySlug = (state: StoreI, slug: AttrSlugType) => {
  const attr = getAttributeBySlug(state, slug);
  return (state.user.session.company as CompanyRespWithSettingAttributes).setting_attributes[attr.uuid];
};

export const isAdmin = (store: StoreI) => hasPermission(store, [UserPermissions.ManageCompany]);

const isLoadedLoacalesAndTimezones = (state: SessionState, loadedName: 'timezones' | 'locales') => {
  if (loadedName === 'locales') {
    return !!Object.keys(state.timezones).length;
  } else {
    return !!Object.keys(state.contentLocales).length;
  }
};

export default sessionReducer;
